import React, { useEffect, useState } from 'react';
import './pwa.css';

/**
 * Meldet, dass eine neue Fassung bereitliegt.
 *
 * ── Warum das zum Service Worker zwingend dazugehört ──────────────────────
 *
 * Ein Service Worker liefert aus dem Cache. Ohne diesen Hinweis liefe ein
 * offener Tab nach einem Deploy dauerhaft mit dem alten Bundle weiter - genau
 * das Problem, das im Projekt schon ohne Worker aufgetreten ist (v0.5.0:
 * „Missing or insufficient permissions", weil das alte Bundle gegen neue
 * Rules lief). Mit Cache wäre es schlimmer, weil nicht einmal ein Neuladen
 * sicher hilft.
 *
 * ── Die zwei Regeln, an denen so etwas üblicherweise scheitert ────────────
 *
 * 1. NICHT neu laden, solange der neue Worker nicht übernommen hat. Ein
 *    Reload davor holt zwangsläufig wieder das alte Bundle aus dem alten
 *    Cache - der Nutzer klickt, es passiert scheinbar nichts.
 * 2. Auf `controllerchange` warten und ERST DANN neu laden. Das ist das
 *    Signal, dass der neue Worker die Seite tatsächlich bedient.
 */
export default function UpdateBanner() {
  const [registration, setRegistration] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onUpdate(event) {
      setRegistration(event.detail);
    }
    window.addEventListener('mt-sw-update', onUpdate);
    return () => window.removeEventListener('mt-sw-update', onUpdate);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let reloaded = false;
    function onControllerChange() {
      // Gegen die Schleife: Ein Reload darf nur einmal ausgelöst werden.
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () =>
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  function activate() {
    const waiting = registration?.waiting;
    if (!waiting) return;

    setBusy(true);
    // Der Worker wartet, bis alle Tabs zu sind - bei einer App also praktisch
    // nie. Diese Nachricht bittet ihn, sofort zu übernehmen; der
    // controllerchange-Handler oben lädt danach neu.
    waiting.postMessage({ type: 'SKIP_WAITING' });
  }

  if (!registration?.waiting) return null;

  // Ab der Zustimmung schrumpft der Hinweis auf eine Zeile: Es gibt nichts
  // mehr zu entscheiden, nur noch zu warten. Der Kasten mit Überschrift und
  // Schaltfläche verdeckte in dieser Phase das geöffnete Menü, ohne noch
  // einen Zweck zu haben (gemeldet am 21.09.2026).
  if (busy) {
    return (
      <div className="mt-pwa mt-pwa--update mt-pwa--progress" role="status" aria-live="polite">
        <span>Neue Version wird geladen …</span>
      </div>
    );
  }

  return (
    <div className="mt-pwa mt-pwa--update" role="status">
      <div className="mt-pwa__text">
        <strong>Neue Version verfügbar</strong>
        <span>Ein Neustart dauert nur einen Moment.</span>
      </div>
      <div className="mt-pwa__actions">
        <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={activate}>
          Jetzt aktualisieren
        </button>
      </div>
    </div>
  );
}
